import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AppBar,
    Avatar,
    Badge,
    Box,
    Button,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Radio,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    AccountBalanceWallet as WalletIcon,
    Assessment as ReportIcon,
    BusinessCenter as CompanyIcon,
    CheckCircle as CheckCircleIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Close as CloseIcon,
    KeyboardArrowDown,
    Logout,
    Menu as MenuIcon,
    Payments as PaymentsIcon,
    PendingActions as PendingIcon,
    Search as SearchIcon,
    Settings as SettingsIcon,
    Work as WorkIcon,
} from '@mui/icons-material';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '../../contexts/auth.context';
import {EmployeeRole} from "../../models/employee.model";
import {GuestCompany, loadGuestCompanies, LoginResponse, storeAuthData} from "../../services/auth.service";
import {ResponseStatus} from "../../models/api-response.model";
import {switchCompany} from "../../services/user-account.service";

const expandedDrawerWidth = 300;
const collapsedDrawerWidth = 72;

interface MainLayoutProps {
    children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({children}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [sidebarExpanded, setSidebarExpanded] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [pendingsOpen, setPendingsOpen] = useState(false);
    const [hrNeedsOpen, setHrNeedsOpen] = useState(false);
    const [billingPaymentsOpen, setBillingPaymentsOpen] = useState(false);
    const [companyPeopleOpen, setCompanyPeopleOpen] = useState(false);
    const [reportsOpen, setReportsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const {user, logout} = useAuth();
    const [companyNameAnchor, setCompanyNameAnchor] = useState<HTMLElement | null>(null)
    const [showCompanySwitch, setShowCompanySwitch] = useState(false)
    const [companies, setCompanies] = useState<GuestCompany[]>([])
    const [allCompanies, setAllCompanies] = useState<GuestCompany[]>([])
    const [alertMessage, setAlertMessage] = useState('')
    const [alertType, setAlertType] = useState('')
    const [companySearch, setCompanySearch] = useState('')
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null)


    useEffect(() => {
        if (!user?.companyId) {
            return;
        }
        loadGuestCompanies().then(response => {
            if (response.status !== ResponseStatus.SUCCESS) {
                return;
            }
            setAllCompanies(response.body);
            setCompanies(response.body);
        })
    }, [user?.companyId])

    const filteredCompanies = useMemo(() => {
        if (!companySearch.trim()) return allCompanies;
        return allCompanies.filter(c =>
            c.name.toLowerCase().includes(companySearch.toLowerCase())
        );
    }, [allCompanies, companySearch]);

    const currentDrawerWidth = sidebarExpanded ? expandedDrawerWidth : collapsedDrawerWidth;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleSidebarToggle = () => {
        setSidebarExpanded(!sidebarExpanded);
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        handleProfileMenuClose();
    };

    const onRoles = useCallback((roles: EmployeeRole[]) => {
        if (roles.length === 0) {
            return true;
        }
        const loginResponse = (user as LoginResponse);
        if (loginResponse?.roles.length === 0) {
            return false;
        }
        return roles?.some(role => loginResponse?.roles.includes(role));
    }, [user])

    const settingsSubItems = useMemo(() => ({
        visible: onRoles([EmployeeRole.SYSTEM_ADMIN]),
        groups: [
            {
                label: 'General',
                items: [
                    {
                        visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER, EmployeeRole.PROJECT_MANAGER, EmployeeRole.SYSTEM_ADMIN]),
                        text: 'Companies',
                        path: '/settings/companies'
                    },
                    {
                        visible: onRoles([EmployeeRole.SYSTEM_ADMIN]),
                        text: 'Payment Methods',
                        path: '/settings/banks'
                    },
                    {
                        visible: onRoles([EmployeeRole.SYSTEM_ADMIN]),
                        text: 'File Types',
                        path: '/settings/file-types'
                    },
                    {
                        visible: onRoles([EmployeeRole.SYSTEM_ADMIN]),
                        text: 'Flow Builder',
                        path: '/settings/flow-builder'
                    },
                ]
            },
            {
                label: 'HR Setup',
                items: [
                    {
                        visible: onRoles([EmployeeRole.SYSTEM_ADMIN]),
                        text: 'Leave Policies',
                        path: '/settings/leave-policies'
                    },
                    {
                        visible: onRoles([EmployeeRole.SYSTEM_ADMIN]),
                        text: 'Holidays',
                        path: '/settings/holidays'
                    },
                    {
                        visible: onRoles([EmployeeRole.SYSTEM_ADMIN]),
                        text: 'Commission Percentage',
                        path: '/settings/commission-percentage'
                    },
                ]
            },
        ]
    }), [onRoles]);

    const settingsFlatItems = settingsSubItems.groups.flatMap(group => group.items);

    const handleSettingsClick = () => {
        setSettingsOpen(!settingsOpen);
    };

    const handlePendingsClick = () => {
        setPendingsOpen(!pendingsOpen);
    };

    const handleHrNeedsClick = () => {
        setHrNeedsOpen(!hrNeedsOpen);
    };

    const handleBillingPaymentsClick = () => {
        setBillingPaymentsOpen(!billingPaymentsOpen);
    };

    const handleCompanyPeopleClick = () => {
        setCompanyPeopleOpen(!companyPeopleOpen);
    };

    const handleReportsClick = () => {
        setReportsOpen(!reportsOpen);
    };

    const pendingSubItems = useMemo(() => ({
        items: [
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Proformas',
                path: '/pending-proformas'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Labour Payments',
                path: '/pending-labour-payments'
            },
            {
                visible: onRoles([]),
                text: 'Work Flows',
                path: '/workflow-actions'
            },
        ]
    }), [onRoles]);

    const pendingItemsVisible = pendingSubItems.items.some(item => item.visible);

    // Check if any pendings-related path is active
    const isPendingsActive = pendingSubItems.items.some(item => location.pathname === item.path);

    const billingPaymentsSubItems = useMemo(() => ({
        items: [
            {
                visible: onRoles([]),
                text: 'New Proforma',
                path: '/proforma'
            },
            {
                visible: onRoles([]),
                text: 'New Labour Payment',
                path: '/labour-payment'
            },
            {
                visible: onRoles([]),
                text: 'My Proformas',
                path: '/my-proformas'
            },
            {
                visible: onRoles([]),
                text: 'My Labour Payments',
                path: '/my-labour-payments'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Labour Payments',
                path: '/labour-payments'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Proformas',
                path: '/proformas'
            },
            {
                visible: onRoles([]),
                text: 'My Commission Claims',
                path: '/my-commission-claims'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Commission Claims',
                path: '/commission-claims'
            },
        ]
    }), [onRoles]);

    const billingPaymentsItemsVisible = billingPaymentsSubItems.items.some(item => item.visible);

    // Check if any Payment & Claims-related path is active
    const isBillingPaymentsActive = billingPaymentsSubItems.items.some(item => location.pathname === item.path);

    const companyPeopleSubItems = useMemo(() => ({
        items: [
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER, EmployeeRole.SYSTEM_ADMIN]),
                text: 'Employees',
                path: '/employees'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER, EmployeeRole.PROJECT_MANAGER, EmployeeRole.SYSTEM_ADMIN]),
                text: 'Projects',
                path: '/projects'
            },
        ]
    }), [onRoles]);

    const companyPeopleItemsVisible = companyPeopleSubItems.items.some(item => item.visible);

    // Check if any Company & People-related path is active
    const isCompanyPeopleActive = companyPeopleSubItems.items.some(item => location.pathname === item.path);

    const reportsSubItems = useMemo(() => ({
        items: [
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Proformas',
                path: '/reports?subject=proformas'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Labour Payments',
                path: '/reports?subject=labour-payments'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Leaves',
                path: '/reports?subject=leaves'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Salary Advances',
                path: '/reports?subject=salary-advances'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Commission Claims',
                path: '/reports?subject=commission-claims'
            },
        ]
    }), [onRoles]);

    const reportsItemsVisible = reportsSubItems.items.some(item => item.visible);

    const isReportsActive = location.pathname === '/reports';

    const hrNeedsSubItems = useMemo(() => ({
        items: [
            {
                visible: onRoles([]),
                text: 'My Salary Advances',
                path: '/my-salary-advances'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Salary Advances',
                path: '/salary-advances'
            },
            {
                visible: onRoles([]),
                text: 'My Leaves',
                path: '/my-leaves'
            },
            {
                visible: onRoles([EmployeeRole.MANAGING_DIRECTOR, EmployeeRole.PAYMENT_APPROVER, EmployeeRole.ACCOUNTANT, EmployeeRole.PAYMENT_AUTHORIZER]),
                text: 'Leave Requests',
                path: '/leaves'
            },
        ]
    }), [onRoles]);

    const hrNeedsItemsVisible = hrNeedsSubItems.items.some(item => item.visible);

    // Check if any HR Needs-related path is active
    const isHrNeedsActive = hrNeedsSubItems.items.some(item => location.pathname === item.path);

    // Check if any settings-related path is active
    const isSettingsActive = location.pathname.startsWith('/settings');

    // Auto-expand the group containing the active route (covers direct URL navigation)
    useEffect(() => {
        if (billingPaymentsSubItems.items.some(item => item.path === location.pathname)) {
            setBillingPaymentsOpen(true);
        }
        if (pendingSubItems.items.some(item => item.path === location.pathname)) {
            setPendingsOpen(true);
        }
        if (hrNeedsSubItems.items.some(item => item.path === location.pathname)) {
            setHrNeedsOpen(true);
        }
        if (companyPeopleSubItems.items.some(item => item.path === location.pathname)) {
            setCompanyPeopleOpen(true);
        }
        if (location.pathname === '/reports') {
            setReportsOpen(true);
        }
        if (location.pathname.startsWith('/settings')) {
            setSettingsOpen(true);
        }
    }, [
        location.pathname,
        billingPaymentsSubItems,
        pendingSubItems,
        hrNeedsSubItems,
        companyPeopleSubItems,
    ]);

    const handleSwitchCompany = (companyId: number) => {
        switchCompany({companyId}).then(response => {
            setAlertMessage(response.message);
            if (response.status !== ResponseStatus.SUCCESS) {
                setShowCompanySwitch(false);
                setAlertType('danger')
                return;
            }
            storeAuthData(response.body);
            setShowCompanySwitch(false);
            setAlertType('success');
            setTimeout(() => window.location.reload(), 1000);
        })
    }

    const drawer = (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f8f9fa',
                boxShadow: '0 0 15px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s ease',
            }}
        >
            {/* Header with logo and toggle */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: sidebarExpanded ? 2 : 1.5,
                    minHeight: 64,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                    color: 'white',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                }}
            >
                {sidebarExpanded ? (
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            letterSpacing: '0.5px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <WorkIcon fontSize="small"/> Sura
                    </Typography>
                ) : (
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            width: '100%',
                            textAlign: 'center'
                        }}
                    >
                        S
                    </Typography>
                )}
                {!isMobile && (
                    <IconButton
                        onClick={handleSidebarToggle}
                        sx={{
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease',
                        }}
                        size="small"
                    >
                        {sidebarExpanded ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
                    </IconButton>
                )}
            </Box>

            {/* User Profile Section */}
            <Box
                sx={{
                    p: sidebarExpanded ? 2 : 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    transition: 'all 0.3s ease',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: sidebarExpanded ? 2 : 0,
                        justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                        cursor: 'pointer',
                        padding: 1,
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                        },
                        transition: 'all 0.2s ease',
                    }}
                    onClick={handleProfileMenuOpen}
                >
                    <Badge
                        overlap="circular"
                        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                        variant="dot"
                        color="success"
                    >
                        <Avatar
                            sx={{
                                width: sidebarExpanded ? 42 : 34,
                                height: sidebarExpanded ? 42 : 34,
                                bgcolor: theme.palette.primary.main,
                                fontSize: sidebarExpanded ? '1.2rem' : '1rem',
                                border: `2px solid ${theme.palette.background.paper}`,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {user?.firstName?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                    </Badge>
                    {sidebarExpanded && (
                        <Box sx={{minWidth: 0, flex: 1}}>
                            <Typography variant="subtitle2" noWrap sx={{fontWeight: 600}}>
                                {user?.firstName || 'User'}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    opacity: 0.7
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: 'success.main',
                                        display: 'inline-block',
                                    }}
                                />
                                Online
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            {/* Navigation Menu */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    py: 1.5,
                    '&::-webkit-scrollbar': {
                        width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                    },
                }}
            >
                <Typography
                    variant="overline"
                    sx={{
                        display: sidebarExpanded ? 'block' : 'none',
                        px: 3,
                        mb: 1,
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        opacity: 0.7,
                        letterSpacing: 1,
                    }}
                >
                    Navigation
                </Typography>

                <List sx={{
                    px: 1.5,
                    '& .MuiListItem-root': {
                        mb: 1,
                    }
                }}>
                    {/* Payment & Claims expandable section */}
                    {billingPaymentsItemsVisible && (
                        <ListItem disablePadding>
                            <Tooltip title={!sidebarExpanded ? 'Payment & Claims' : ''} placement="right">
                                <ListItemButton
                                    selected={isBillingPaymentsActive}
                                    onClick={() => {
                                        if (sidebarExpanded) {
                                            handleBillingPaymentsClick();
                                        } else {
                                            // If collapsed, expand sidebar and open billing & payments
                                            setSidebarExpanded(true);
                                            setBillingPaymentsOpen(true);
                                        }
                                    }}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 48,
                                        justifyContent: sidebarExpanded ? 'initial' : 'center',
                                        px: sidebarExpanded ? 2.5 : 1.5,
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 700,
                                            },
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        position: 'relative',
                                    }}
                                >
                                    {isBillingPaymentsActive && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                width: 4,
                                                height: '75%',
                                                bgcolor: theme.palette.primary.light,
                                                display: !sidebarExpanded ? 'block' : 'none',
                                                borderRadius: '0 4px 4px 0',
                                                boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
                                            }}
                                        />
                                    )}
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: sidebarExpanded ? 0.5 : 'auto',
                                            justifyContent: 'center',
                                            color: isBillingPaymentsActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        <PaymentsIcon/>
                                    </ListItemIcon>
                                    {sidebarExpanded && (
                                        <>
                                            <ListItemText
                                                primary="Payment & Claims"
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '0.9rem',
                                                        fontWeight: isBillingPaymentsActive ? 700 : 500,
                                                    },
                                                }}
                                            />
                                            {billingPaymentsOpen ?
                                                <KeyboardArrowDown sx={{transition: 'transform 0.3s ease'}}/> :
                                                <ChevronRightIcon sx={{transition: 'transform 0.3s ease'}}/>}
                                        </>
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    )}
                    {/* Collapsible Payment & Claims sub-items */}
                    {sidebarExpanded && billingPaymentsItemsVisible && (
                        <Collapse in={billingPaymentsOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{pl: 2, pr: 1}}>
                                {billingPaymentsSubItems.items.filter(item => item.visible).map((item, index, array) => {
                                    const isSubActive = location.pathname === item.path;
                                    const isLast = index === array.length - 1;
                                    return (
                                        <ListItem key={item.text} disablePadding sx={{mb: 0.2, position: 'relative'}}>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 16,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    width: 13,
                                                    height: 2,
                                                    zIndex: 1,
                                                    bgcolor: isSubActive
                                                        ? theme.palette.primary.main
                                                        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)'),
                                                }}
                                            />
                                            {!isLast && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 16,
                                                        top: '50%',
                                                        bottom: 'calc(-50% - 4px)',
                                                        width: 2,
                                                        zIndex: 1,
                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            )}
                                            <ListItemButton
                                                selected={isSubActive}
                                                onClick={() => {
                                                    navigate(item.path);
                                                    if (isMobile) setMobileOpen(false);
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    minHeight: 44,
                                                    my: '2px',
                                                    pl: 4,
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.1)'
                                                            : 'transparent',
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemIcon-root': {
                                                            color: theme.palette.primary.main,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemText-primary': {
                                                            fontWeight: 700,
                                                        },
                                                    },
                                                    transition: 'all 0.2s ease-in-out',
                                                }}
                                            >
                                                <ListItemText
                                                    primary={item.text}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            fontSize: '0.85rem',
                                                            fontWeight: isSubActive ? 700 : 500,
                                                        },
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Collapse>
                    )}

                    {/* Pendings expandable section */}
                    {pendingItemsVisible && (
                        <ListItem disablePadding>
                            <Tooltip title={!sidebarExpanded ? 'Pendings' : ''} placement="right">
                                <ListItemButton
                                    selected={isPendingsActive}
                                    onClick={() => {
                                        if (sidebarExpanded) {
                                            handlePendingsClick();
                                        } else {
                                            // If collapsed, expand sidebar and open pendings
                                            setSidebarExpanded(true);
                                            setPendingsOpen(true);
                                        }
                                    }}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 48,
                                        justifyContent: sidebarExpanded ? 'initial' : 'center',
                                        px: sidebarExpanded ? 2.5 : 1.5,
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 700,
                                            },
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        position: 'relative',
                                    }}
                                >
                                    {isPendingsActive && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                width: 4,
                                                height: '75%',
                                                bgcolor: theme.palette.primary.light,
                                                display: !sidebarExpanded ? 'block' : 'none',
                                                borderRadius: '0 4px 4px 0',
                                                boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
                                            }}
                                        />
                                    )}
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: sidebarExpanded ? 0.5 : 'auto',
                                            justifyContent: 'center',
                                            color: isPendingsActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        <PendingIcon/>
                                    </ListItemIcon>
                                    {sidebarExpanded && (
                                        <>
                                            <ListItemText
                                                primary="Pendings"
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '0.9rem',
                                                        fontWeight: isPendingsActive ? 700 : 500,
                                                    },
                                                }}
                                            />
                                            {pendingsOpen ?
                                                <KeyboardArrowDown sx={{transition: 'transform 0.3s ease'}}/> :
                                                <ChevronRightIcon sx={{transition: 'transform 0.3s ease'}}/>}
                                        </>
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    )}
                    {/* Collapsible pendings sub-items */}
                    {sidebarExpanded && pendingItemsVisible && (
                        <Collapse in={pendingsOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{pl: 2, pr: 1}}>
                                {pendingSubItems.items.filter(item => item.visible).map((item, index, array) => {
                                    const isSubActive = location.pathname === item.path;
                                    const isLast = index === array.length - 1;
                                    return (
                                        <ListItem key={item.text} disablePadding sx={{mb: 0.2, position: 'relative'}}>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 16,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    width: 13,
                                                    height: 2,
                                                    zIndex: 1,
                                                    bgcolor: isSubActive
                                                        ? theme.palette.primary.main
                                                        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)'),
                                                }}
                                            />
                                            {!isLast && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 16,
                                                        top: '50%',
                                                        bottom: 'calc(-50% - 4px)',
                                                        width: 2,
                                                        zIndex: 1,
                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            )}
                                            <ListItemButton
                                                selected={isSubActive}
                                                onClick={() => {
                                                    navigate(item.path);
                                                    if (isMobile) setMobileOpen(false);
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    minHeight: 44,
                                                    my: '2px',
                                                    pl: 4,
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.1)'
                                                            : 'transparent',
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemIcon-root': {
                                                            color: theme.palette.primary.main,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemText-primary': {
                                                            fontWeight: 700,
                                                        },
                                                    },
                                                    transition: 'all 0.2s ease-in-out',
                                                }}
                                            >
                                                <ListItemText
                                                    primary={item.text}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            fontSize: '0.85rem',
                                                            fontWeight: isSubActive ? 700 : 500,
                                                        },
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Collapse>
                    )}

                    {/* HR Needs expandable section */}
                    {hrNeedsItemsVisible && (
                        <ListItem disablePadding>
                            <Tooltip title={!sidebarExpanded ? 'HR Center' : ''} placement="right">
                                <ListItemButton
                                    selected={isHrNeedsActive}
                                    onClick={() => {
                                        if (sidebarExpanded) {
                                            handleHrNeedsClick();
                                        } else {
                                            // If collapsed, expand sidebar and open HR needs
                                            setSidebarExpanded(true);
                                            setHrNeedsOpen(true);
                                        }
                                    }}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 48,
                                        justifyContent: sidebarExpanded ? 'initial' : 'center',
                                        px: sidebarExpanded ? 2.5 : 1.5,
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 700,
                                            },
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        position: 'relative',
                                    }}
                                >
                                    {isHrNeedsActive && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                width: 4,
                                                height: '75%',
                                                bgcolor: theme.palette.primary.light,
                                                display: !sidebarExpanded ? 'block' : 'none',
                                                borderRadius: '0 4px 4px 0',
                                                boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
                                            }}
                                        />
                                    )}
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: sidebarExpanded ? 0.5 : 'auto',
                                            justifyContent: 'center',
                                            color: isHrNeedsActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        <WalletIcon/>
                                    </ListItemIcon>
                                    {sidebarExpanded && (
                                        <>
                                            <ListItemText
                                                primary="HR Center"
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '0.9rem',
                                                        fontWeight: isHrNeedsActive ? 700 : 500,
                                                    },
                                                }}
                                            />
                                            {hrNeedsOpen ?
                                                <KeyboardArrowDown sx={{transition: 'transform 0.3s ease'}}/> :
                                                <ChevronRightIcon sx={{transition: 'transform 0.3s ease'}}/>}
                                        </>
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    )}
                    {/* Collapsible HR Needs sub-items */}
                    {sidebarExpanded && hrNeedsItemsVisible && (
                        <Collapse in={hrNeedsOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{pl: 2, pr: 1}}>
                                {hrNeedsSubItems.items.filter(item => item.visible).map((item, index, array) => {
                                    const isSubActive = location.pathname === item.path;
                                    const isLast = index === array.length - 1;
                                    return (
                                        <ListItem key={item.text} disablePadding sx={{mb: 0.2, position: 'relative'}}>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 16,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    width: 13,
                                                    height: 2,
                                                    zIndex: 1,
                                                    bgcolor: isSubActive
                                                        ? theme.palette.primary.main
                                                        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)'),
                                                }}
                                            />
                                            {!isLast && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 16,
                                                        top: '50%',
                                                        bottom: 'calc(-50% - 4px)',
                                                        width: 2,
                                                        zIndex: 1,
                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            )}
                                            <ListItemButton
                                                selected={isSubActive}
                                                onClick={() => {
                                                    navigate(item.path);
                                                    if (isMobile) setMobileOpen(false);
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    minHeight: 44,
                                                    my: '2px',
                                                    pl: 4,
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.1)'
                                                            : 'transparent',
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemIcon-root': {
                                                            color: theme.palette.primary.main,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemText-primary': {
                                                            fontWeight: 700,
                                                        },
                                                    },
                                                    transition: 'all 0.2s ease-in-out',
                                                }}
                                            >
                                                <ListItemText
                                                    primary={item.text}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            fontSize: '0.85rem',
                                                            fontWeight: isSubActive ? 700 : 500,
                                                        },
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Collapse>
                    )}

                    {/* Company & People expandable section */}
                    {companyPeopleItemsVisible && (
                        <ListItem disablePadding>
                            <Tooltip title={!sidebarExpanded ? 'Company & People' : ''} placement="right">
                                <ListItemButton
                                    selected={isCompanyPeopleActive}
                                    onClick={() => {
                                        if (sidebarExpanded) {
                                            handleCompanyPeopleClick();
                                        } else {
                                            // If collapsed, expand sidebar and open company & people
                                            setSidebarExpanded(true);
                                            setCompanyPeopleOpen(true);
                                        }
                                    }}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 48,
                                        justifyContent: sidebarExpanded ? 'initial' : 'center',
                                        px: sidebarExpanded ? 2.5 : 1.5,
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 700,
                                            },
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        position: 'relative',
                                    }}
                                >
                                    {isCompanyPeopleActive && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                width: 4,
                                                height: '75%',
                                                bgcolor: theme.palette.primary.light,
                                                display: !sidebarExpanded ? 'block' : 'none',
                                                borderRadius: '0 4px 4px 0',
                                                boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
                                            }}
                                        />
                                    )}
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: sidebarExpanded ? 0.5 : 'auto',
                                            justifyContent: 'center',
                                            color: isCompanyPeopleActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        <CompanyIcon/>
                                    </ListItemIcon>
                                    {sidebarExpanded && (
                                        <>
                                            <ListItemText
                                                primary="Company & People"
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '0.9rem',
                                                        fontWeight: isCompanyPeopleActive ? 700 : 500,
                                                    },
                                                }}
                                            />
                                            {companyPeopleOpen ?
                                                <KeyboardArrowDown sx={{transition: 'transform 0.3s ease'}}/> :
                                                <ChevronRightIcon sx={{transition: 'transform 0.3s ease'}}/>}
                                        </>
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    )}
                    {/* Collapsible Company & People sub-items */}
                    {sidebarExpanded && companyPeopleItemsVisible && (
                        <Collapse in={companyPeopleOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{pl: 2, pr: 1}}>
                                {companyPeopleSubItems.items.filter(item => item.visible).map((item, index, array) => {
                                    const isSubActive = location.pathname === item.path;
                                    const isLast = index === array.length - 1;
                                    return (
                                        <ListItem key={item.text} disablePadding sx={{mb: 0.2, position: 'relative'}}>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 16,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    width: 13,
                                                    height: 2,
                                                    zIndex: 1,
                                                    bgcolor: isSubActive
                                                        ? theme.palette.primary.main
                                                        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)'),
                                                }}
                                            />
                                            {!isLast && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 16,
                                                        top: '50%',
                                                        bottom: 'calc(-50% - 4px)',
                                                        width: 2,
                                                        zIndex: 1,
                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            )}
                                            <ListItemButton
                                                selected={isSubActive}
                                                onClick={() => {
                                                    navigate(item.path);
                                                    if (isMobile) setMobileOpen(false);
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    minHeight: 44,
                                                    my: '2px',
                                                    pl: 4,
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.1)'
                                                            : 'transparent',
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemIcon-root': {
                                                            color: theme.palette.primary.main,
                                                        },
                                                    },
                                                    '&:hover': {
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemText-primary': {
                                                            fontWeight: 700,
                                                        },
                                                    },
                                                    transition: 'all 0.2s ease-in-out',
                                                }}
                                            >
                                                <ListItemText
                                                    primary={item.text}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            fontSize: '0.85rem',
                                                            fontWeight: isSubActive ? 700 : 500,
                                                        },
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Collapse>
                    )}
                    {/* Reports expandable section */}
                    {reportsItemsVisible && (
                        <ListItem disablePadding>
                            <Tooltip title={!sidebarExpanded ? 'Summary Reports' : ''} placement="right">
                                <ListItemButton
                                    selected={isReportsActive}
                                    onClick={() => {
                                        if (sidebarExpanded) {
                                            handleReportsClick();
                                        } else {
                                            setSidebarExpanded(true);
                                            setReportsOpen(true);
                                        }
                                    }}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 48,
                                        justifyContent: sidebarExpanded ? 'initial' : 'center',
                                        px: sidebarExpanded ? 2.5 : 1.5,
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 700,
                                            },
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        position: 'relative',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: sidebarExpanded ? 0.5 : 'auto',
                                            justifyContent: 'center',
                                            color: isReportsActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        <ReportIcon/>
                                    </ListItemIcon>
                                    {sidebarExpanded && (
                                        <>
                                            <ListItemText
                                                primary="Summary Reports"
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '0.9rem',
                                                        fontWeight: isReportsActive ? 700 : 500,
                                                    },
                                                }}
                                            />
                                            {reportsOpen ?
                                                <KeyboardArrowDown sx={{transition: 'transform 0.3s ease'}}/> :
                                                <ChevronRightIcon sx={{transition: 'transform 0.3s ease'}}/>}
                                        </>
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    )}
                    {sidebarExpanded && reportsItemsVisible && (
                        <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{pl: 2, pr: 1}}>
                                {reportsSubItems.items.filter(item => item.visible).map((item, index, array) => {
                                    const fullPath = item.path;
                                    const [base, query] = fullPath.split('?');
                                    const params = new URLSearchParams(query || '');
                                    const subject = params.get('subject');
                                    const currentParams = new URLSearchParams(location.search);
                                    const isSubActive = location.pathname === base && currentParams.get('subject') === subject;
                                    const isLast = index === array.length - 1;
                                    return (
                                        <ListItem key={item.text} disablePadding sx={{mb: 0.2, position: 'relative'}}>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 16,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    width: 13,
                                                    height: 2,
                                                    zIndex: 1,
                                                    bgcolor: isSubActive
                                                        ? theme.palette.primary.main
                                                        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)'),
                                                }}
                                            />
                                            {!isLast && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 16,
                                                        top: '50%',
                                                        bottom: 'calc(-50% - 4px)',
                                                        width: 2,
                                                        zIndex: 1,
                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            )}
                                            <ListItemButton
                                                selected={isSubActive}
                                                onClick={() => {
                                                    navigate(fullPath);
                                                    if (isMobile) setMobileOpen(false);
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    minHeight: 44,
                                                    my: '2px',
                                                    pl: 4,
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.1)'
                                                            : 'transparent',
                                                        color: theme.palette.primary.main,
                                                    },
                                                    '&:hover': {
                                                        color: theme.palette.primary.main,
                                                        '& .MuiListItemText-primary': {
                                                            fontWeight: 700,
                                                        },
                                                    },
                                                    transition: 'all 0.2s ease-in-out',
                                                }}
                                            >
                                                <ListItemText
                                                    primary={item.text}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            fontSize: '0.85rem',
                                                            fontWeight: isSubActive ? 700 : 500,
                                                        },
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Collapse>
                    )}
                </List>

                <Divider sx={{my: 1.5, mx: 2}}/>
                <Typography
                    variant="overline"
                    sx={{
                        display: sidebarExpanded ? 'block' : 'none',
                        px: 3,
                        mb: 1,
                        mt: 1,
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        opacity: 0.7,
                        letterSpacing: 1,
                    }}
                >
                    System
                </Typography>


                <List sx={{
                    px: 1.5,
                    '& .MuiListItem-root': {
                        mb: 1,
                    }
                }}>
                    {/* Settings expandable section */}
                    {settingsSubItems.visible && (
                        <ListItem disablePadding>
                            <Tooltip title={!sidebarExpanded ? 'Settings' : ''} placement="right">
                                <ListItemButton
                                    selected={isSettingsActive}
                                    onClick={() => {
                                        if (sidebarExpanded) {
                                            handleSettingsClick();
                                        } else {
                                            // If collapsed, expand sidebar and open settings
                                            setSidebarExpanded(true);
                                            setSettingsOpen(true);
                                        }
                                    }}
                                    sx={{
                                        borderRadius: 2,
                                        minHeight: 48,
                                        justifyContent: sidebarExpanded ? 'initial' : 'center',
                                        px: sidebarExpanded ? 2.5 : 1.5,
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 700,
                                            },
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        position: 'relative',
                                    }}
                                >
                                    {isSettingsActive && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                width: 4,
                                                height: '75%',
                                                bgcolor: theme.palette.primary.light,
                                                display: !sidebarExpanded ? 'block' : 'none',
                                                borderRadius: '0 4px 4px 0',
                                                boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
                                            }}
                                        />
                                    )}
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: sidebarExpanded ? 0.5 : 'auto',
                                            justifyContent: 'center',
                                            color: isSettingsActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        <SettingsIcon/>
                                    </ListItemIcon>
                                    {sidebarExpanded && (
                                        <>
                                            <ListItemText
                                                primary="Settings"
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '0.9rem',
                                                        fontWeight: isSettingsActive ? 700 : 500,
                                                    },
                                                }}
                                            />
                                            {settingsOpen ?
                                                <KeyboardArrowDown sx={{transition: 'transform 0.3s ease'}}/> :
                                                <ChevronRightIcon sx={{transition: 'transform 0.3s ease'}}/>}
                                        </>
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    )}
                    {/* Collapsible settings sub-items */}
                    {sidebarExpanded && settingsSubItems.visible && (
                        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{pl: 2, pr: 1}}>
                                {settingsSubItems.groups.map(group => {
                                    const groupItems = group.items.filter(item => item.visible);
                                    if (groupItems.length === 0) {
                                        return null;
                                    }
                                    return (
                                        <Box key={group.label}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    pl: 2,
                                                    pt: 1,
                                                    pb: 0.5,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.08em',
                                                    fontSize: '0.65rem',
                                                    color: theme.palette.text.secondary,
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                {group.label}
                                            </Typography>
                                            {groupItems.map((item, index) => {
                                                const isSubActive = location.pathname === item.path;
                                                const isLast = index === groupItems.length - 1;
                                                return (
                                                    <ListItem key={item.text} disablePadding sx={{mb: 0.2, position: 'relative'}}>
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                left: 24,
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                width: 13,
                                                                height: 2,
                                                                zIndex: 1,
                                                                bgcolor: isSubActive
                                                                    ? theme.palette.primary.main
                                                                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)'),
                                                            }}
                                                        />
                                                        {!isLast && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    left: 24,
                                                                    top: '50%',
                                                                    bottom: 'calc(-50% - 4px)',
                                                                    width: 2,
                                                                    zIndex: 1,
                                                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                                                                    borderRadius: 1,
                                                                }}
                                                            />
                                                        )}
                                                        <ListItemButton
                                                            selected={isSubActive}
                                                            onClick={() => {
                                                                navigate(item.path);
                                                                if (isMobile) setMobileOpen(false);
                                                            }}
                                                            sx={{
                                                                    borderRadius: 2,
                                                                    minHeight: 44,
                                                                    my: '2px',
                                                                    pl: 4.5,
                                                                '&.Mui-selected': {
                                                                    backgroundColor: theme.palette.mode === 'dark'
                                                                        ? 'rgba(255,255,255,0.1)'
                                                                        : 'transparent',
                                                                    color: theme.palette.primary.main,
                                                                    '& .MuiListItemIcon-root': {
                                                                        color: theme.palette.primary.main,
                                                                    },
                                                                },
                                                                '&:hover': {
                                                                    color: theme.palette.primary.main,
                                                                    '& .MuiListItemText-primary': {
                                                                        fontWeight: 700,
                                                                    },
                                                                },
                                                                transition: 'all 0.2s ease-in-out',
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={item.text}
                                                                sx={{
                                                                    '& .MuiListItemText-primary': {
                                                                        fontSize: '0.85rem',
                                                                        fontWeight: isSubActive ? 700 : 500,
                                                                    },
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                );
                                            })}
                                        </Box>
                                    );
                                })}
                            </List>
                        </Collapse>
                    )}
                </List>
            </Box>
        </Box>
    );

    return (
        <Box sx={{display: 'flex'}}>
            <AppBar
                position="fixed"
                sx={{
                    width: {md: `calc(100% - ${currentDrawerWidth}px)`},
                    ml: {md: `${currentDrawerWidth}px`},
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{mr: 2, display: {md: 'none'}}}
                    >
                        <MenuIcon/>
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{flexGrow: 1}}>
                        {companyPeopleSubItems.items.find(item => item.path === location.pathname)?.text ||
                            billingPaymentsSubItems.items.find(item => item.path === location.pathname)?.text ||
                            pendingSubItems.items.find(item => item.path === location.pathname)?.text ||
                            hrNeedsSubItems.items.find(item => item.path === location.pathname)?.text ||
                            settingsFlatItems.find(item => item.path === location.pathname)?.text ||
                            (location.pathname.startsWith('/settings') ? 'Settings' : 'Dashboard')}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        {/* Company Name + Switch Button */}
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mr: 2}}>
                            <CompanyIcon sx={{color: '#4338ca', fontSize: 22}}/>
                            <Box sx={{borderLeft: '2px solid #4338ca', pl: 1, display: {xs: 'none', md: 'block'}}}>
                                <Typography variant="body2" fontWeight={700} color="#1e293b" sx={{lineHeight: 1.2}}>
                                    {user?.companyName || 'Company'}
                                </Typography>
                                <Typography variant="caption" color="#64748b" sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    <Box component="span" sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', display: 'inline-block'}}/>
                                    Current Company
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                endIcon={<KeyboardArrowDown/>}
                                onClick={(e) => {
                                    setCompanyNameAnchor(e.currentTarget);
                                    setShowCompanySwitch(true);
                                    setCompanySearch('');
                                    setSelectedCompanyId(null);
                                }}
                                sx={{
                                    ml: 1,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    borderColor: '#cbd5e1',
                                    color: '#1e293b',
                                    bgcolor: '#f8fafc',
                                    '&:hover': { borderColor: '#4338ca', color: '#4338ca', bgcolor: '#eef2ff' },
                                }}
                            >
                                Switch Company
                            </Button>
                        </Box>

                        {/* Switch Company Popover */}
                        <Menu
                            anchorEl={companyNameAnchor}
                            open={showCompanySwitch}
                            onClose={() => setShowCompanySwitch(false)}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                                    width: 380,
                                    maxHeight: 'none',
                                    mt: 1,
                                    overflow: 'visible',
                                }
                            }}
                        >
                            {/* Header */}
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2, pb: 1.5}}>
                                <Typography variant="h6" fontWeight={700} color="#1e293b" sx={{fontSize: '1.1rem'}}>
                                    Switch Company
                                </Typography>
                                <IconButton onClick={() => setShowCompanySwitch(false)} size="small">
                                    <CloseIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                            {/* Search */}
                            <Box sx={{px: 2.5, pb: 1}}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search Companies"
                                    value={companySearch}
                                    onChange={(e) => setCompanySearch(e.target.value)}
                                    autoFocus
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{color: '#94a3b8', mr: 1, fontSize: 20}}/>,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4338ca' },
                                        }
                                    }}
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </Box>
                            {/* Count */}
                            <Typography variant="caption" color="#64748b" sx={{px: 2.5, mb: 0.5, display: 'block'}}>
                                Available Companies ({filteredCompanies.length})
                            </Typography>
                            {/* Company List */}
                            <Box sx={{maxHeight: 320, overflowY: 'auto', px: 1.5, pb: 2}}>
                                {filteredCompanies.map(company => {
                                    const isCurrentCompany = company.id === user?.companyId;
                                    const isSelected = selectedCompanyId === company.id;
                                    return (
                                        <Box
                                            key={company.id}
                                            onClick={() => {
                                                if (!isCurrentCompany) {
                                                    setSelectedCompanyId(company.id);
                                                    handleSwitchCompany(company.id);
                                                }
                                            }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                px: 1.5,
                                                py: 1,
                                                borderRadius: '8px',
                                                cursor: isCurrentCompany ? 'default' : 'pointer',
                                                bgcolor: isCurrentCompany ? '#eef2ff' : 'transparent',
                                                '&:hover': {
                                                    bgcolor: isCurrentCompany ? '#eef2ff' : '#f8fafc',
                                                },
                                            }}
                                        >
                                            {isCurrentCompany ? (
                                                <CheckCircleIcon sx={{fontSize: 22, color: '#10b981'}}/>
                                            ) : (
                                                <Radio
                                                    checked={isSelected}
                                                    size="small"
                                                    sx={{
                                                        p: 0,
                                                        color: '#cbd5e1',
                                                        '&.Mui-checked': { color: '#4338ca' },
                                                    }}
                                                />
                                            )}
                                            <CompanyIcon sx={{fontSize: 20, color: '#4338ca'}}/>
                                            <Typography variant="body2" fontWeight={isCurrentCompany ? 600 : 400} color="#1e293b">
                                                {company.name}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Menu>

                        {alertMessage && alertType === 'success' &&
                            <Alert severity='success' onClose={() => setAlertMessage('')}>{alertMessage}</Alert>}
                        {alertMessage && alertType === 'danger' &&
                            <Alert severity='error' onClose={() => setAlertMessage('')}>{alertMessage}</Alert>}

                        {/* User Info */}
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Typography variant="body2" sx={{display: {xs: 'none', sm: 'block'}}}>
                                {user?.firstName || 'User'}
                            </Typography>
                            <IconButton
                                size="large"
                                edge="end"
                                aria-label="account of current user"
                                aria-controls="profile-menu"
                                aria-haspopup="true"
                                onClick={handleProfileMenuOpen}
                                color="inherit"
                            >
                                <Avatar sx={{width: 32, height: 32, bgcolor: theme.palette.primary.main}}>
                                    {user?.firstName?.charAt(0).toUpperCase() || 'U'}
                                </Avatar>
                            </IconButton>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
                onClick={handleProfileMenuClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            >
                <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
                    <Avatar/> Profile
                </MenuItem>
                <Divider/>
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <Logout fontSize="small"/>
                    </ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>

            <Box
                component="nav"
                sx={{width: {md: currentDrawerWidth}, flexShrink: {md: 0}}}
                aria-label="navigation"
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: {xs: 'block', md: 'none'},
                        '& .MuiDrawer-paper': {boxSizing: 'border-box', width: expandedDrawerWidth},
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: {xs: 'none', md: 'block'},
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: currentDrawerWidth,
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            overflowX: 'hidden',
                            borderRight: `1px solid ${theme.palette.divider}`,
                            boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: {md: `calc(100% - ${currentDrawerWidth}px)`},
                    minHeight: '100vh',
                    backgroundColor: theme.palette.grey[50],
                }}
            >
                <Toolbar/>
                {children}
            </Box>
        </Box>
    );
};

export default MainLayout;
